
import React, { useState, useRef, useEffect, useLayoutEffect, forwardRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import * as S from "./ChatInterfaceStyles";
import ChatHeader from "./ChatHeader";
import MessageList from "./MessageList";
import ChatInput from "./ChatInput";
import RightPanel from "./RightPanel";
import { preprocessMessage } from "./MessagePreprocessor"; // Import the new preprocessor
import { SummaryPayload, SummaryExchange } from "../types/summary";
import { ChipType } from "./ChipList";
import SupportIcon from "@mui/icons-material/Support";
import CodeIcon from "@mui/icons-material/Code";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import DescriptionIcon from "@mui/icons-material/Description";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { apiUrl } from "../utils/api";
import { deployedAgentsStreamUrl, fetchDeployedAgents, fetchWorkflows } from "../adk-agent-builder/api";
import { Briefcase } from "lucide-react";
import ManualWorkflowApp from "./ManualWorkflowApp";
import ManualWorkflowTrigger from "./ManualWorkflowTrigger";
import ServiceDeskWorkflowApp from "./ServiceDeskWorkflowApp";
import ServiceDeskWorkflowTrigger from "./ServiceDeskWorkflowTrigger";

type SummaryCache = {
  summary: SummaryPayload;
  source: "llm" | "fallback" | null;
  signature: string;
  updatedAt: number;
};

const buildSummarySignature = (summary?: SummaryPayload) => {
  if (!summary) {
    return "";
  }
  const overview = summary.overview ?? "";
  const rawSummary = summary.raw_summary ?? "";
  const points = summary.summary_points ?? [];
  return [overview, rawSummary, ...points].join("||");
};

const readSummaryCache = (email?: string | null): SummaryCache | null => {
  if (!email) {
    return null;
  }
  try {
    const cached = localStorage.getItem(`activitySummaryCache:${email}`);
    if (!cached) {
      return null;
    }
    const parsed = JSON.parse(cached) as SummaryCache;
    if (!parsed?.summary || !parsed?.signature) {
      return null;
    }
    return parsed;
  } catch (error) {
    console.warn("Failed to read summary cache", error);
    return null;
  }
};

const writeSummaryCache = (email: string, cache: SummaryCache) => {
  try {
    localStorage.setItem(`activitySummaryCache:${email}`, JSON.stringify(cache));
  } catch (error) {
    console.warn("Failed to write summary cache", error);
  }
};

export interface Attachment {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  file_url: string;
  gcs_uri?: string;
  local_path?: string;
  preview_url?: string;
}

interface Message {
  sender: string;
  content: string;
  chips?: ChipType[];
  pdf_path?: string;
  recipient_email?: string;
  attachments?: Attachment[];
  folder_info?: {
    name: string;
    file_count: number;
  };
}

interface ChatInterfaceProps {
  agent: string | null;
  sessionId: string | null;
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  input: string;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  sendMessage: (
    messageContent: string | { label: string; pdf_path?: string; recipient_email?: string },
    filePath?: string,
    options?: {
      sessionOverride?: string;
      agentOverride?: string | null;
      skipPersist?: boolean;
      attachments?: Attachment[];
      folder_info?: {
        name: string;
        file_count: number;
      };
    }
  ) => Promise<void>;
  handleFileUpload: (files: FileList) => Promise<string>;
  handleAudioUpload: (file: File) => Promise<string>;
  error: string | null;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  startSession: (agentInfo: string | { label: string; agent: string; }) => Promise<void>;
}


interface ChatInterfaceHandle {
  startNewConversation: () => void;
}

const isInterviewSchedulingEnabled = (agent?: string | null) => {
  if (!agent) return false;
  const normalized = agent.toLowerCase();
  const interviewAgents = [
    "hr_assistant_agent",
    "hr assistant agent",
    "recruitment assistant",
    "recruitment_assistant_agent",
    "interview_scheduler_agent",
    "interview scheduler",
  ];
  return interviewAgents.some((name) => normalized.includes(name));
};

type NotificationItem = {
  id: number;
  title: string;
  body: string;
  category: string;
  severity: string;
  created_at: string;
  is_read: boolean;
};

const ChatInterface = forwardRef<ChatInterfaceHandle, ChatInterfaceProps>(
  (props, ref) => {
    const { messages, setMessages, input, setInput, sendMessage, handleFileUpload, handleAudioUpload, error: _error, isLoading: _isLoading, setIsLoading: _setIsLoading, agent, sessionId, startSession } = props;
    const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(true);
    const [rightPanelWidth, setRightPanelWidth] = useState(320);
    const [rightPanelMode, setRightPanelMode] = useState<"summary" | "notifications" | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isAgentResponding, setIsAgentResponding] = useState(false);
    const [notifications, setNotifications] = useState<NotificationItem[]>([]);
    const [notificationsLoading, setNotificationsLoading] = useState(false);
    const [notificationsError, setNotificationsError] = useState<string | null>(null);
    const [summaryHistory, setSummaryHistory] = useState<SummaryExchange[]>([]);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summaryError, setSummaryError] = useState<string | null>(null);
    const rightPanelRef = useRef<HTMLDivElement>(null);
    const messageContainerRef = useRef<HTMLDivElement>(null);
    const lastMessageRef = useRef<HTMLDivElement>(null);
    const summaryHistoryRef = useRef<SummaryExchange[]>([]);
    const summarySignatureRef = useRef<string | null>(null);
    const { user, token } = useAuth();

    // Manual Workflow State
    const [isManualWorkflowOpen, setIsManualWorkflowOpen] = useState(false);
    const [hasDismissedManualWorkflow, setHasDismissedManualWorkflow] = useState(false);
    const [isServiceDeskOpen, setIsServiceDeskOpen] = useState(false);
    const [hasDismissedServiceDesk, setHasDismissedServiceDesk] = useState(false);

    // Feature flags – controls whether summary fetching happens
    const [summarizationEnabled, setSummarizationEnabled] = useState(true);

    const [userWorkspaces, setUserWorkspaces] = useState<any[]>([]);
    const [selectedWorkspace, setSelectedWorkspace] = useState<any | null>(null);
    const [workflows, setWorkflows] = useState<any[]>([]);

    const invokeManualTool = async (tool: string, params: any) => {
      if (!agent || !sessionId) throw new Error("No active session");
      
      const payload = {
        tool: tool,
        params: params,
        appName: agent,
        sessionId: sessionId,
      };

      const executeUrl = `${apiUrl("/chat/manual_execute")}?user_email=${encodeURIComponent(user?.email || "user")}`;
      const res = await fetch(executeUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Failed to execute manual tool: ${errText}`);
      }

      const result = await res.json();
      return result;
    };

    useEffect(() => {
      if (user?.role?.toLowerCase() === 'viewer') return;

      const fetchWs = async () => {
        try {
          const response = await fetch(apiUrl('/api/workspaces/'), {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setUserWorkspaces(data);
            if (data.length === 1) {
              setSelectedWorkspace(data[0]);
            }
          }
        } catch (err) {
          console.error("Failed to fetch workspaces", err);
        }
      };

      if (token) {
        fetchWs();
      }
    }, [token, user?.role]);

    useEffect(() => {
      fetchWorkflows().then(setWorkflows).catch(() => { });
    }, []);

    useEffect(() => {
      const event = new CustomEvent('workspaceUpdate', {
        detail: { selectedWorkspace, workflows, userWorkspaces }
      });
      window.dispatchEvent(event);
    }, [selectedWorkspace, workflows, userWorkspaces]);


    const wrappedSendMessage = async (messageContent: string, filePath?: string, attachments?: Attachment[], folder_info?: { name: string; file_count: number }) => {
      console.log("wrappedSendMessage: Setting isAgentResponding to true");
      setIsAgentResponding(true);
      try {
        await sendMessage(messageContent, filePath, { attachments, folder_info });
      } finally {
        console.log("wrappedSendMessage: sendMessage promise resolved/rejected.");
        setIsAgentResponding(false);
      }
    };

    const handleFileUploadChange = async (files: FileList): Promise<string> => {
      return await handleFileUpload(files);
    };

    useLayoutEffect(() => {
      const messageContainer = messageContainerRef.current;
      if (messageContainer) {
        messageContainer.scrollTo({
          top: messageContainer.scrollHeight,
          behavior: "smooth",
        });
      }
    }, [messages]);

    useEffect(() => {
      console.log("ChatInterface: messages updated:", messages);
      console.log("ChatInterface: isAgentResponding state:", isAgentResponding);
    }, [messages, isAgentResponding]);

    const fetchNotifications = useCallback(async () => {
      setNotificationsLoading(true);
      setNotificationsError(null);
      try {
        const params: Record<string, string | number | boolean> = {
          include_read: false,
          limit: 25,
        };
        if (user?.email) {
          params.user_id = user.email;
        }
        const response = await axios.get<NotificationItem[]>(apiUrl("/notifications"), { params });
        setNotifications(response.data);
      } catch (err) {
        console.error("Failed to fetch notifications", err);
        setNotificationsError("Unable to load notifications. Please try again.");
      } finally {
        setNotificationsLoading(false);
      }
    }, [user?.email]);

    const fetchSummary = useCallback(
      async (options?: { useCache?: boolean; background?: boolean }) => {
        if (!user?.email) {
          setSummaryError("Please log in to view your activity summary.");
          return;
        }

        // Skip backend call if summarization is disabled – show cached data only
        if (!summarizationEnabled) {
          const cached = readSummaryCache(user.email);
          if (cached) {
            setSummaryHistory([
              {
                id: `cached-${cached.updatedAt}`,
                prompt: "Activity Summary",
                status: "ready",
                summary: cached.summary,
                source: cached.source ?? null,
                requestedAt: cached.updatedAt,
              },
            ]);
          }
          return;
        }

        const cached = options?.useCache ? readSummaryCache(user.email) : null;
        if (cached && !summaryHistoryRef.current.length) {
          setSummaryHistory([
            {
              id: `cached-${cached.updatedAt}`,
              prompt: "Activity Summary",
              status: "ready",
              summary: cached.summary,
              source: cached.source ?? null,
              requestedAt: cached.updatedAt,
            },
          ]);
        }

        if (!options?.background || !cached) {
          const exchangeId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
          setSummaryHistory([
            {
              id: exchangeId,
              prompt: "Activity Summary",
              status: "pending",
              requestedAt: Date.now(),
            },
          ]);
        }

        setSummaryLoading(true);
        setSummaryError(null);
        try {
          const response = await axios.get(apiUrl("/summary/weekly-v2"), {
            params: {
              user_email: user?.email,
              memory_limit: 50,
              notifications_limit: 10,
            },
          });

          const summaryPayload = response.data.summary as SummaryPayload;
          const source = (response.data.summary_source as "llm" | "fallback") || null;
          const signature = buildSummarySignature(summaryPayload);
          const previousSignature = cached?.signature || summarySignatureRef.current;

          const cacheEntry: SummaryCache = {
            summary: summaryPayload,
            source,
            signature,
            updatedAt: Date.now(),
          };

          if (signature && signature !== previousSignature) {
            writeSummaryCache(user.email, cacheEntry);
          }

          if (!options?.background || !cached || signature !== previousSignature) {
            setSummaryHistory([
              {
                id: `summary-${cacheEntry.updatedAt}`,
                prompt: "Activity Summary",
                status: "ready",
                summary: summaryPayload,
                source,
                requestedAt: cacheEntry.updatedAt,
              },
            ]);
          }
        } catch (err) {
          console.error("Failed to generate weekly summary", err);
          setSummaryError("Summary is unavailable. Please try again later.");
        } finally {
          setSummaryLoading(false);
        }
      },
      [user?.email, summarizationEnabled]
    );

    useEffect(() => {
      if (!user?.email) {
        setSummaryHistory([]);
        return;
      }
      const cached = readSummaryCache(user.email);
      if (cached) {
        setSummaryHistory([
          {
            id: `cached-${cached.updatedAt}`,
            prompt: "Activity Summary",
            status: "ready",
            summary: cached.summary,
            source: cached.source ?? null,
            requestedAt: cached.updatedAt,
          },
        ]);
      }
      fetchSummary({ useCache: true, background: true });
    }, [fetchSummary, user?.email, sessionId]);

    // Fetch feature flags to control summary fetching
    useEffect(() => {
      const fetchFlags = async () => {
        try {
          const response = await axios.get(apiUrl("/api/features"));
          setSummarizationEnabled(response.data.summarization_enabled ?? true);
        } catch {
          // Default to enabled if fetch fails
          setSummarizationEnabled(true);
        }
      };
      fetchFlags();
    }, [sessionId]);

    useEffect(() => {
      summaryHistoryRef.current = summaryHistory;
      const latest = summaryHistory[0];
      summarySignatureRef.current = latest?.summary ? buildSummarySignature(latest.summary) : null;
    }, [summaryHistory]);

    const startResize = (e: React.MouseEvent) => {
      setIsDragging(true);
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", stopResize);
      e.preventDefault();
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && !isRightPanelCollapsed) {
        const proposed = window.innerWidth - e.clientX;
        const minWidth = 220;
        const maxWidth = Math.max(260, window.innerWidth - 480);
        const clamped = Math.min(Math.max(proposed, minWidth), maxWidth);
        setRightPanelWidth(clamped);
      }
    };

    const stopResize = () => {
      setIsDragging(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", stopResize);
    };

    const toggleRightPanel = () => setIsRightPanelCollapsed(!isRightPanelCollapsed);

    const getHeaderTitle = () => {
      return "Unified AI Agents Platform";
    };

    const [deployedAgents, setDeployedAgents] = useState<Array<{ id?: string; name: string; identifier: string; description: string }>>([]);

    useEffect(() => {
      let isMounted = true;
      let eventSource: EventSource | null = null;

      const load = async () => {
        try {
          const agents = await fetchDeployedAgents();
          if (isMounted) {
            setDeployedAgents(agents);
          }
        } catch {
          // Ignore refresh errors.
        }
      };

      load();
      eventSource = new EventSource(deployedAgentsStreamUrl);
      eventSource.onmessage = () => {
        load();
      };
      eventSource.onerror = () => {
        // Keep the last known list if the stream drops.
      };

      return () => {
        isMounted = false;
        if (eventSource) {
          eventSource.close();
        }
      };
    }, []);

    // Truncate description to first sentence or 60 chars
    const truncateDesc = (desc: string) => {
      if (!desc) return '';
      const firstSentence = desc.split('.')[0];
      return firstSentence.length > 60 ? firstSentence.slice(0, 60) + '...' : firstSentence + '.';
    };

    const visibleAgents = React.useMemo(() => {
      const parseAgentIds = (rawAgentIds: unknown): string[] => {
        try {
          const parsed = typeof rawAgentIds === 'string' ? JSON.parse(rawAgentIds) : (rawAgentIds ?? []);
          return Array.isArray(parsed) ? parsed.map((id) => String(id)) : [];
        } catch {
          return Array.isArray(rawAgentIds) ? rawAgentIds.map((id) => String(id)) : [];
        }
      };

      const mapWorkflowIdsToIdentifiers = (workflowIds: string[]) => {
        const allowedIdentifiers = new Set<string>();

        workflowIds.forEach((workflowId) => {
          const normalizedWorkflowId = String(workflowId).toLowerCase();
          if (!normalizedWorkflowId) {
            return;
          }

          const deployedAgentMatch = deployedAgents.find(
            (agent) => String(agent.id ?? '').toLowerCase() === normalizedWorkflowId
          );
          if (deployedAgentMatch?.identifier) {
            allowedIdentifiers.add(deployedAgentMatch.identifier.toLowerCase());
            return;
          }

          const workflowMatch = workflows.find(
            (workflow) => String(workflow?.id ?? '').toLowerCase() === normalizedWorkflowId
          );
          if (workflowMatch?.identifier) {
            allowedIdentifiers.add(String(workflowMatch.identifier).toLowerCase());
            return;
          }

          allowedIdentifiers.add(normalizedWorkflowId);
        });

        return allowedIdentifiers;
      };

      const matchesAllowedAgent = (
        deployedAgent: { name: string; identifier: string },
        allowedIdentifiers: Set<string>
      ) => {
        const identifier = deployedAgent.identifier?.toLowerCase?.() ?? '';
        const name = deployedAgent.name?.toLowerCase?.() ?? '';
        return allowedIdentifiers.has(identifier) || allowedIdentifiers.has(name);
      };

      if (user?.role?.toLowerCase() === 'viewer') {
        return deployedAgents.filter(agent => agent.name.toLowerCase().includes('greeting') || agent.identifier.toLowerCase().includes('greeting'));
      }

      if (user?.role?.toLowerCase() === 'super admin') {
        return deployedAgents;
      }

      if (user?.role?.toLowerCase() === 'org admin') {
        // Backend /workflows already enforces org-admin visibility. Use that first
        // so chat still shows agents even when workspace memberships are missing.
        const workflowScopedIdentifiers = new Set<string>(
          workflows
            .map((workflow) => String(workflow?.identifier ?? '').toLowerCase())
            .filter(Boolean)
        );

        if (workflowScopedIdentifiers.size > 0) {
          return deployedAgents.filter((agent) => matchesAllowedAgent(agent, workflowScopedIdentifiers));
        }

        const workspaceAgentIds: string[] = [];
        userWorkspaces.forEach((ws) => {
          workspaceAgentIds.push(...parseAgentIds(ws.agentIds));
        });

        const workspaceScopedIdentifiers = mapWorkflowIdsToIdentifiers(workspaceAgentIds);
        return deployedAgents.filter((agent) => matchesAllowedAgent(agent, workspaceScopedIdentifiers));
      }

      if (!selectedWorkspace) return [];

      const workspaceAgentIds = parseAgentIds(selectedWorkspace.agentIds);
      if (workspaceAgentIds.length === 0) return [];

      const allowedIdentifiers = mapWorkflowIdsToIdentifiers(workspaceAgentIds);
      return deployedAgents.filter((agent) => matchesAllowedAgent(agent, allowedIdentifiers));
    }, [user?.role, deployedAgents, selectedWorkspace, workflows, userWorkspaces]);

    const suggestions = [
      // All agents come from deployed workflows
      ...visibleAgents.map((agent, idx) => ({
        id: 100 + idx,
        title: ` ${agent.name.replace(/_/g, ' ')}`,
        description: truncateDesc(agent.description) || `Custom agent.`,
        agent: agent.identifier,
        isCustom: true,
      })),
    ];

    const handleSuggestionClick = async (suggestion: typeof suggestions[0]) => {
      await startSession({ label: suggestion.title.trim(), agent: suggestion.agent });
    };

    const handleShowNotifications = () => {
      setIsRightPanelCollapsed((collapsed) => {
        if (!collapsed && rightPanelMode === "notifications") {
          return true;
        }
        setRightPanelMode("notifications");
        fetchNotifications();
        return false;
      });
    };

    const isSuperOrOrgAdmin = user?.role?.toLowerCase() === 'super admin' || user?.role?.toLowerCase() === 'org admin';

    const handleShowSummary = () => {
      setIsRightPanelCollapsed((collapsed) => {
        if (!collapsed && rightPanelMode === "summary") {
          return true;
        }
        setRightPanelMode("summary");
        return false;
      });
    };



    useEffect(() => {
      if (isRightPanelCollapsed) {
        return;
      }
      if (rightPanelMode === "notifications") {
        fetchNotifications();
      }
      if (rightPanelMode === "summary") {
        fetchSummary({ useCache: true, background: true });
      }
    }, [isRightPanelCollapsed, rightPanelMode, fetchNotifications, fetchSummary]);

    const isIngestionContext = React.useMemo(() => {
      if (!messages || messages.length === 0) return false;
      const recentMessages = messages.slice(-5);
      return recentMessages.some(m =>
        (typeof m.content === 'string' && m.content.toLowerCase().includes('ingest')) ||
        (m.sender === 'System' && typeof m.content === 'string' && m.content.toLowerCase().includes('ingest'))
      );
    }, [messages]);

    return (
      <S.MainContent $rightPanelWidth={isRightPanelCollapsed ? 0 : rightPanelWidth}>
        <S.ChatContainer $expanded={true}>
          <ChatHeader
            title={getHeaderTitle()}
            onNewConversation={() => setMessages([])}
            onShowNotifications={handleShowNotifications}
            onShowSummary={handleShowSummary}
            onShowManualWorkflow={
              (agent === "recruitment_management_agent" || agent === "Recruitment_Management") 
                ? () => setIsManualWorkflowOpen(true) 
                : (agent === "service_desk_agent" || agent === "Service_Desk_Management")
                  ? () => setIsServiceDeskOpen(true)
                  : undefined
            }
          />

          <S.MessageContainer ref={messageContainerRef}>
            {messages.length === 0 ? (
              <>
                <S.WelcomeCard>
                  <S.WelcomeTitle>The Contextual AI Platform for Intelligent Workforce Decisions</S.WelcomeTitle>
                </S.WelcomeCard>

                {!isSuperOrOrgAdmin && userWorkspaces.length > 1 && !selectedWorkspace ? (
                  <div className="flex flex-col items-center justify-center w-full max-w-5xl mx-auto mt-12 mb-8">
                    <div className="text-center mb-10">
                      <h3 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 mb-3">Select a Workspace</h3>
                      <p className="text-slate-500 dark:text-slate-400 text-lg">Choose your environment to start collaborating</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 w-full px-6">
                      {userWorkspaces.map(ws => (
                        <div
                          key={ws.id}
                          className="relative group p-8 bg-white dark:bg-slate-800/80 backdrop-blur-sm rounded-3xl border border-slate-200/60 dark:border-slate-700/50 hover:border-blue-500/50 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_30px_-4px_rgba(59,130,246,0.15)] cursor-pointer overflow-hidden transition-all duration-300 ease-out transform hover:-translate-y-1 flex flex-col items-center text-center"
                          onClick={() => setSelectedWorkspace(ws)}
                        >
                          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 dark:from-blue-900/10 dark:to-indigo-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                          <div className="relative z-10 w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/40 dark:to-indigo-900/40 rounded-2xl flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform duration-300">
                            <Briefcase size={28} className="text-blue-600 dark:text-blue-400" />
                          </div>
                          <h4 className="relative z-10 text-xl font-semibold text-slate-800 dark:text-slate-100 mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{ws.name}</h4>
                          <p className="relative z-10 text-sm md:text-base text-slate-500 dark:text-slate-400 line-clamp-3 leading-relaxed">{ws.description || "Access agents and workflows for this workspace."}</p>

                          <div className="relative z-10 mt-6 pt-6 border-t border-slate-100 dark:border-slate-700/50 w-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                            <span className="text-sm font-medium text-blue-600 dark:text-blue-400 flex items-center gap-1">
                              Enter Workspace
                              <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    {!isSuperOrOrgAdmin && userWorkspaces.length > 1 && selectedWorkspace && (
                      <div className="flex justify-center mb-8 mt-2">
                        <button
                          onClick={() => setSelectedWorkspace(null)}
                          className="group flex items-center gap-2 px-5 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-200 dark:hover:border-blue-800 hover:shadow-md transition-all duration-200"
                        >
                          <Briefcase size={16} className="text-slate-400 group-hover:text-blue-500 transition-colors" />
                          <span>Workspace: <span className="font-semibold text-slate-800 dark:text-slate-100">{selectedWorkspace.name}</span></span>
                          <span className="ml-2 text-xs px-2.5 py-1 bg-slate-100 dark:bg-slate-700 rounded-full text-slate-500 dark:text-slate-400 group-hover:bg-blue-50 dark:group-hover:bg-blue-900/40 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">Change</span>
                        </button>
                      </div>
                    )}
                    {(!isSuperOrOrgAdmin && userWorkspaces.length === 0) ? (
                      <div className="flex flex-col items-center justify-center w-full mt-8">
                        <p className="text-slate-500">You don't have access to any workspaces.</p>
                      </div>
                    ) : (
                      <S.SuggestionGrid>
                        {suggestions.map((suggestion) => (
                          <S.SuggestionCard
                            key={suggestion.id}
                            onClick={() => handleSuggestionClick(suggestion)}
                          >
                            <S.SuggestionBody>
                              <S.SuggestionIcon style={{ backgroundColor: "#10B98117" }}>
                                <SupportIcon style={{ width: "20px", height: "20px", color: "#10B981" }} />
                              </S.SuggestionIcon>
                              <S.SuggestionContent>
                                <S.SuggestionTitle>{suggestion.title}</S.SuggestionTitle>
                                <S.SuggestionDescription>{suggestion.description}</S.SuggestionDescription>
                              </S.SuggestionContent>
                            </S.SuggestionBody>
                            <S.SuggestionArrow>›</S.SuggestionArrow>
                          </S.SuggestionCard>
                        ))}
                      </S.SuggestionGrid>
                    )}
                    {(!selectedWorkspace && !isSuperOrOrgAdmin && userWorkspaces.length > 1) || (!isSuperOrOrgAdmin && userWorkspaces.length === 0) ? null : (
                      <ChatInput
                        input={input}
                        setInput={setInput}
                        sendMessage={wrappedSendMessage}
                        handleFileUpload={handleFileUploadChange}
                        handleAudioUpload={handleAudioUpload}
                        isEmpty={true}
                        isIngestionContext={isIngestionContext}
                      />
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                {!hasDismissedManualWorkflow && (agent === "recruitment_management_agent" || agent === "Recruitment_Management") && (
                  <ManualWorkflowTrigger 
                    onSwitchToManual={() => {
                      setIsManualWorkflowOpen(true);
                      setHasDismissedManualWorkflow(true);
                    }}
                    onContinueInChat={() => setHasDismissedManualWorkflow(true)}
                  />
                )}
                {!hasDismissedServiceDesk && (agent === "service_desk_agent" || agent === "Service_Desk_Management") && (
                  <ServiceDeskWorkflowTrigger 
                    onSwitchToManual={() => {
                      setIsServiceDeskOpen(true);
                      setHasDismissedServiceDesk(true);
                    }}
                    onContinueInChat={() => setHasDismissedServiceDesk(true)}
                  />
                )}
                <MessageList
                  lastMessageRef={lastMessageRef}
                  messages={messages}
                preprocessMessage={preprocessMessage}
                isLoading={isAgentResponding}
                onChipClick={async (chip, message) => {
                  console.log("Chip clicked:", chip);
                  console.log("Message data:", message);

                  setInput(chip.label);
                  setIsAgentResponding(true);
                  try {
                    // Use chip.label as the message text.  Do NOT override the
                    // agent — the chip's "agent" field is a conceptual workflow
                    // label (e.g. "ClientContextAgent"), not a real ADK appName.
                    // Overriding it caused 404 on ADK because that module doesn't
                    // exist as a standalone agent.
                    await sendMessage(chip.label);
                  } finally {
                    setIsAgentResponding(false);
                  }
                }}
                onFormSubmit={async (values) => {
                  setMessages((prevMessages) => [
                    ...prevMessages,
                    { sender: "You", content: "details submitted" },
                  ]);
                  setIsAgentResponding(true);
                  await sendMessage(JSON.stringify({ intent: "form_submit", values }));
                }}
                onCardAction={async (action, card) => {
                  setIsAgentResponding(true);
                  let message = action.label || "Action";
                  if (action.action?.startsWith("view_profile")) {
                    const target = action.action.split("|")[1] || card.title;
                    message = `Show me the profile for ${target}`;
                  } else if (action.action?.startsWith("schedule_interview")) {
                    const target = action.action.split("|")[1] || card.title;
                    message = `Schedule an interview with ${target}`;
                  } else {
                    message = `Perform ${action.label} on ${card.title}`;
                  }
                  await sendMessage(message);
                }}
                onScheduleInterview={isInterviewSchedulingEnabled(agent) ? async (candidateName: string) => {
                  const interviewMessage = `Schedule Interview with ${candidateName}`;
                  await sendMessage(interviewMessage);
                } : undefined}
                />
                </>
                )}
                </S.MessageContainer>

                {messages.length > 0 && (
                <ChatInput
                input={input}
                setInput={setInput}
                sendMessage={wrappedSendMessage}
                handleFileUpload={handleFileUploadChange}
                handleAudioUpload={handleAudioUpload}
                isEmpty={false}
                isIngestionContext={isIngestionContext}
                />
                )}
                </S.ChatContainer>

                <ManualWorkflowApp 
                isOpen={isManualWorkflowOpen} 
                onClose={() => setIsManualWorkflowOpen(false)} 
                onInvokeTool={invokeManualTool} 
                />
                
                <ServiceDeskWorkflowApp 
                isOpen={isServiceDeskOpen} 
                onClose={() => setIsServiceDeskOpen(false)} 
                onInvokeTool={invokeManualTool} 
                />

                <RightPanel          width={rightPanelWidth}
          isCollapsed={isRightPanelCollapsed}
          onToggle={toggleRightPanel}
          onResizeStart={startResize}
          rightPanelRef={rightPanelRef}
          mode={rightPanelMode}
          notifications={notifications}
          notificationsLoading={notificationsLoading}
          notificationsError={notificationsError}
          onRefreshNotifications={fetchNotifications}
          summaryHistory={summaryHistory}
          summaryLoading={summaryLoading}
          summaryError={summaryError}
          summarizationEnabled={summarizationEnabled}
        />
      </S.MainContent>
    );
  }
);

export default React.memo(ChatInterface) as typeof ChatInterface;
